// src/contexts/ThemeContext.js

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
// Importe createTheme para ter acesso às sombras padrão
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from '@mui/material/CssBaseline';
import { useAuthContext } from "./AuthContext";
import api from "../services/api";
import { debounce } from "lodash";

const AppThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const { user, setUser } = useAuthContext();

  const defaultPalette = useMemo(() => ({
    primary: "#1976d2",
    secondary: "#9c27b0",
    error: "#d32f2f",
    warning: "#ed6c02",
    info: "#0288d1",
    success: "#2e7d32",
    backgroundDefault: "#f0f2f5",
    backgroundPaper: "#ffffff",
    textPrimary: "#333333",
    textSecondary: "#666666",
    divider: "#e0e0e0",
    grey50: "#fafafa",
    grey100: "#f5f5f5",
    grey200: "#eeeeee",
    grey300: "#e0e0e0",
  }), []);

  const [palette, setPalette] = useState(defaultPalette);

  useEffect(() => {
    if (user?.tema) {
      try {
        const parsed = typeof user.tema === "string" ? JSON.parse(user.tema) : user.tema;
        const merged = {
          ...defaultPalette,
          primary: parsed?.primary || defaultPalette.primary,
          secondary: parsed?.secondary || defaultPalette.secondary,
          error: parsed?.error || defaultPalette.error,
          warning: parsed?.warning || defaultPalette.warning,
          info: parsed?.info || defaultPalette.info,
          success: parsed?.success || defaultPalette.success,
          backgroundDefault: parsed?.backgroundDefault || defaultPalette.backgroundDefault,
          backgroundPaper: parsed?.backgroundPaper || defaultPalette.backgroundPaper,
          textPrimary: parsed?.textPrimary || defaultPalette.textPrimary,
          textSecondary: parsed?.textSecondary || defaultPalette.textSecondary,
          divider: parsed?.divider || defaultPalette.divider,
          grey50: parsed?.grey50 || defaultPalette.grey50,
          grey100: parsed?.grey100 || defaultPalette.grey100,
          grey200: parsed?.grey200 || defaultPalette.grey200,
          grey300: parsed?.grey300 || defaultPalette.grey300,
        };
        if (JSON.stringify(merged) !== JSON.stringify(palette)) {
          console.log("🎨 Tema atualizado do banco de dados:", merged);
          setPalette(merged);
        }
      } catch (error) {
        console.error("❌ Erro ao carregar tema:", error);
        setPalette(defaultPalette);
      }
    } else {
      setPalette(defaultPalette);
    }
  }, [user?.tema, defaultPalette]);

  // Crie um tema temporário para obter as sombras padrão
  const defaultMuiTheme = useMemo(() => createTheme(), []);

  const theme = useMemo(() => {
    // Copie as sombras padrão e sobrescreva as que você customizou
    const customShadows = [...defaultMuiTheme.shadows];
    customShadows[1] = '0px 2px 4px rgba(0, 0, 0, 0.05)'; // shadow[1] - leve para cards
    customShadows[2] = '0px 4px 8px rgba(0, 0, 0, 0.08)'; // shadow[2]
    customShadows[3] = '0px 6px 12px rgba(0, 0, 0, 0.1)'; // shadow[3] - mais pronunciada para containers principais
    // Se você tiver outras elevações customizadas, defina-as aqui:
    // customShadows[8] = 'sua sombra para elevation 8';
    // customShadows[24] = 'sua sombra para elevation 24';

    return createTheme({
      palette: {
        primary: {
          main: palette.primary,
          light: palette.primary + 'B3',
          dark: palette.primary + '80',
          contrastText: '#fff',
        },
        secondary: {
          main: palette.secondary,
          contrastText: '#fff',
        },
        error: { main: palette.error },
        warning: { main: palette.warning },
        info: { main: palette.info },
        success: { main: palette.success },
        background: {
          default: palette.backgroundDefault,
          paper: palette.backgroundPaper,
        },
        text: {
          primary: palette.textPrimary,
          secondary: palette.textSecondary,
        },
        divider: palette.divider,
        grey: {
          50: palette.grey50,
          100: palette.grey100,
          200: palette.grey200,
          300: palette.grey300,
        },
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h4: { fontSize: '1.8rem', fontWeight: 600 },
        h5: { fontSize: '1.5rem', fontWeight: 600 },
        h6: { fontSize: '1.2rem', fontWeight: 500 },
      },
      shape: {
        borderRadius: 8,
      },
      // Use o array de sombras customizado
      shadows: customShadows, // <--- AQUI ESTÁ A MUDANÇA PRINCIPAL
      components: {
        MuiButton: { styleOverrides: { root: { textTransform: 'none' } } },
        MuiCard: { styleOverrides: { root: { borderRadius: 8 } } },
        MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
        MuiSelect: { defaultProps: { variant: 'outlined', size: 'small' } },
        MuiFormControl: { defaultProps: { variant: 'outlined', size: 'small' } },
        MuiChip: { styleOverrides: { root: { fontWeight: 'bold' } } },
        MuiTableHead: { styleOverrides: { root: { backgroundColor: palette.grey100 } } },
        MuiTableCell: { styleOverrides: { head: { fontWeight: 'bold', color: palette.textSecondary } } },
      },
    });
  }, [palette, defaultMuiTheme]); // Adicione defaultMuiTheme como dependência

  const updatePalette = useCallback((key, value) => {
    setPalette((prev) => ({ ...prev, [key]: value }));
  }, []);

  const debouncedSaveTheme = useCallback(
    debounce(() => {
      setPalette((currentPalette) => {
        api
          .post("/api/Usuario/atualizar-tema", { Tema: currentPalette })
          .then((response) => {
            if (response?.data?.success) {
              console.log("✅ Tema salvo com sucesso.");
              setUser((prevUser) => ({
                ...prevUser,
                tema: JSON.stringify(currentPalette),
              }));
            } else {
              console.warn("⚠️ Erro ao salvar tema:", response?.data?.message);
            }
          })
          .catch((error) => {
            console.error("❌ Erro ao salvar tema:", error);
          });
        return currentPalette;
      });
    }, 500),
    [setUser]
  );

  const appThemeContextValue = useMemo(() => {
    return {
      palette,
      updatePalette,
      saveTheme: debouncedSaveTheme,
      setPalette,
    };
  }, [palette, updatePalette, debouncedSaveTheme]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AppThemeContext.Provider value={appThemeContextValue}>
        {children}
      </AppThemeContext.Provider>
    </MuiThemeProvider>
  );
};

export const useAppTheme = () => {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error("useAppTheme deve ser usado dentro de um ThemeProvider");
  }
  return context;
};
